import { type AccountRole, canDo, type WorkspaceRole } from "@/shared/auth/permissions";
import { dropEmptyNavHeadings, isNavHeading } from "@/shared/components/layout/payroll-nav-order";
import type { NavItem } from "@/shared/components/layout/sidebar-nav.config";
import type { FeatureKey } from "@/shared/entitlements/entitlements.constants";
import { resolveFeatureEnabled } from "@/shared/entitlements/hooks/use-feature-enabled";
import type { Entitlements } from "@/shared/entitlements/schemas/entitlements.schema";

/// Permission + plan-feature gate for one sidebar nav item (W4). Split out of
/// `sidebar-nav.tsx` to keep that file under the 250-line component budget
/// (A3); this half is pure and unit-tested without a render harness.
//
// A permitted item whose plan feature is disabled resolves "locked": it
// stays in the sidebar (greyed, with a lock affordance) instead of
// vanishing, so a free-tier user can see what they are missing and reach the
// upgrade prompt (W4). A missing permission still resolves "hidden": no
// amount of upgrading grants a role, so showing an upgrade CTA there would
// be a lie.
export type NavItemGateState = "hidden" | "locked" | "visible";

/** Pure combinator: permission-hide always wins (an item without permission
 *  never reaches the feature check, W4, since an upgrade cannot fix a
 *  missing role). A permitted item whose plan feature is disabled resolves
 *  "locked" instead of hiding, so the upgrade path stays discoverable.
 *  Extracted so the truth table is unit-testable without rendering the
 *  sidebar. */
export function resolveNavItemGate({
  hasPermission,
  featureEnabled,
}: {
  hasPermission: boolean;
  featureEnabled: boolean;
}): NavItemGateState {
  if (!hasPermission) return "hidden";
  if (!featureEnabled) return "locked";
  return "visible";
}

/** Runs one item through the permission + feature gate and returns the
 *  resolved item (with `locked: true` patched in when applicable) or `null`
 *  when it should be dropped. */
function resolveItem(
  item: NavItem,
  accountRole: AccountRole | null,
  workspaceRoles: WorkspaceRole[],
  features: Entitlements["features"],
): NavItem | null {
  const hasPermission = item.requiresAnyOf
    ? item.requiresAnyOf.some((p) => canDo(p, { accountRole, workspaceRoles }))
    : !item.requires || canDo(item.requires, { accountRole, workspaceRoles });
  const featureEnabled =
    !item.requiresFeature || resolveFeatureEnabled(features, item.requiresFeature);
  const gate = resolveNavItemGate({ hasPermission, featureEnabled });
  if (gate === "hidden") return null;
  return gate === "locked" ? { ...item, locked: true } : item;
}

/** Permission + feature gate for one nav item; recurse into a collapsible
 *  parent's children and drop the parent when none survive.
 *  `dynamicOverrides` is a per-render patch keyed by `item.to`, used by the
 *  parent to inject server-state badges (e.g. `newActivity`) onto specific
 *  entries. */
export function keepItem(
  item: NavItem,
  accountRole: AccountRole | null,
  workspaceRoles: WorkspaceRole[],
  features: Entitlements["features"],
  dynamicOverrides: Record<string, Partial<NavItem>> = {},
): NavItem | null {
  if (item.children) {
    const children = item.children
      .map((c) => (isNavHeading(c) ? c : resolveItem(c, accountRole, workspaceRoles, features)))
      .filter((c): c is NavItem => c !== null)
      .map((c) => (isNavHeading(c) ? c : { ...c, ...(dynamicOverrides[c.to] ?? {}) }));
    const visible = dropEmptyNavHeadings(children);
    return visible.length > 0 ? { ...item, children: visible } : null;
  }
  const resolved = resolveItem(item, accountRole, workspaceRoles, features);
  if (!resolved) return null;
  return { ...resolved, ...(dynamicOverrides[item.to] ?? {}) };
}

/** Builds the locked-item click handler bound to its own feature key (avoids
 *  a non-null assertion at the call site: `feature` is only undefined for
 *  items that can never be locked, since `resolveItem` only sets `locked`
 *  when `requiresFeature` was present).
 *
 *  Used by `sidebar-nav.tsx` at each `<Item>` call site: a locked row's click
 *  opens the shared `<UpgradeDialog>` (via the caller's `onLockedClick`)
 *  instead of navigating, since the row underneath renders as a button, not
 *  a `Link`, when `item.locked` is set (`sidebar-nav-item.tsx`). */
export function lockedClickHandler(
  item: NavItem,
  onLockedClick: (feature: FeatureKey) => void,
): (() => void) | undefined {
  const feature = item.requiresFeature;
  return feature ? () => onLockedClick(feature) : undefined;
}
